import { Checkbox, Divider, Input, Tooltip, Typography } from 'antd';
import { produce } from 'immer';
import { useIntl } from 'react-intl';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';

import classnames from 'classnames';

import { Form } from 'common/components';
import { EVENT } from 'static/Constants';
import useDocumentAttachEvent from 'hooks/documentAttachEventHook';
import { updateDynamicContentByTag } from 'utils/email-editor';
import { updateDynamicContent } from '../../sections/product-apl/utils';

import Messages from 'i18n/messages/apl';

let timeout;

const AplSampleRequestDynamicContentForm = (props, ref) => {
  const intl = useIntl();
  const [form] = Form.useForm();

  const [addressSync, setAddressSync] = useState(false);

  const {
    contactEmail,
    setRequestSampleData,
    dynamicContent,
    requestSampleData,
  } = props;

  const onClickEyeIcon = (field) => () => {
    setRequestSampleData((pre) =>
      produce(pre, (draft) => {
        draft[contactEmail].dynamicContent = {
          ...updateDynamicContentByTag({
            prevState: draft[contactEmail].dynamicContent,
            tags: [field],
            field: 'visible',
            value: !dynamicContent[field]?.visible,
          }),
        };
      })
    );
  };

  const onValueChange = (fieldChange, allValues) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      setRequestSampleData((pre) => {
        const data = updateDynamicContent({
          requestSampleData: pre,
          allValues,
          fieldChange,
          contactEmail,
          addressSync,
        });
        return data;
      });
    }, 200);
  };

  const clearValueWhenSectionRemoved = (e) => {
    const tags = e.detail.tags;

    const setValue = tags.reduce((accumulator, currentTag) => {
      accumulator[currentTag] = '';
      return accumulator;
    }, {});

    form.setFieldsValue(setValue);
  };

  useDocumentAttachEvent({
    name: EVENT.EMAIL_EDITOR_REMOVE_SECTION,
    handler: clearValueWhenSectionRemoved,
  });

  const initDynamicContent = () => {
    let _dynamicContent = {};
    Object.keys(dynamicContent)?.forEach((field) => {
      _dynamicContent[field] = dynamicContent[field]?.value || null;
    });

    form.setFieldsValue({
      ..._dynamicContent,
    });
  };

  // Updated when contact email changes
  useEffect(() => {
    initDynamicContent();
  }, [contactEmail]);

  const getFormItemClassName = (field) =>
    classnames({
      'apl-request-sample__dynamic-content-form-item': true,
      'apl-request-sample__dynamic-content-form-item--highlight':
        field.highlight,
    });

  useImperativeHandle(ref, () => {
    return {
      validateForm: () => {
        form.validateFields();
      },
    };
  });

  const showShippingInfoSync = Object.keys(requestSampleData)?.length > 1;

  return (
    <Form
      className='scroller apl-request-sample__dynamic-content-form'
      layout='vertical'
      form={form}
      onValuesChange={onValueChange}
    >
      {Object.keys(dynamicContent)?.map((field) => {
        const fieldData = dynamicContent[field];

        const EyeIcon = fieldData?.visible ? EyeOutlined : EyeInvisibleOutlined;

        return fieldData.show ? (
          <>
            {(field === 'shippingName' || field === 'additionalInfo') && (
              <Divider style={{ margin: '0 0 12px', borderColor: '#bfbfbf' }} />
            )}
            {field === 'shippingName' && showShippingInfoSync ? (
              <Checkbox
                style={{ marginBottom: 8 }}
                onChange={(e) => {
                  setAddressSync(e.target.checked);
                }}
                checked={addressSync}
              >
                <Tooltip title='Shipping information will be synchronized once the checkbox is checked'>
                  <Typography.Text strong>Shipping Info Sync</Typography.Text>
                </Tooltip>
              </Checkbox>
            ) : null}
            <div className='apl-request-sample__dynamic-content-form-item-wrapper'>
              <Form.Item
                className={getFormItemClassName(fieldData)}
                label={intl.formatMessage(Messages[`${field}Label`])}
                name={field}
                key={field}
                rules={[
                  {
                    required: fieldData?.required && fieldData?.visible,
                    message: intl.formatMessage(Messages.requiredMessage),
                  },
                ]}
              >
                {field === 'additionalInfo' ? (
                  <Input.TextArea
                    value={dynamicContent?.additionalInfo?.value}
                    autoSize={{ minRows: 6, maxRows: 6 }}
                  />
                ) : (
                  <Input
                    value={dynamicContent[fieldData?.value]}
                    disabled={!fieldData?.visible}
                  />
                )}
              </Form.Item>
              {fieldData?.eye ? (
                <EyeIcon
                  className='apl-request-sample__dynamic-content-form-show-hide-icon'
                  onClick={onClickEyeIcon(field)}
                />
              ) : null}
            </div>
          </>
        ) : null;
      })}
    </Form>
  );
};

export default forwardRef(AplSampleRequestDynamicContentForm);
